import React from "react";
import { createPortal } from "react-dom";

/**
 * Loading state — neon "Loading" letters scanned by a rotating color blob
 * masked through vertical bars. Lifted from the v0 template, adapted to CRA:
 *   - <style jsx> swapped for global keyframes in src/index.css
 *   - Tailwind arbitrary classes preserved where they fit on one line; the
 *     heavy `mask:` and `background-image:` rules moved to inline style for
 *     readability.
 *
 * Props:
 *   fullScreen  Renders inside a fixed full-viewport overlay (used during
 *               auth / portfolio bootstrap). Portals to document.body so an
 *               ancestor `transform` doesn't trap position: fixed.
 */
const LETTERS = "Loading".split("");

const LoadingLinesInner = () => (
  <div className="relative flex items-center justify-center h-[120px] w-auto m-8 text-[1.6em] font-semibold select-none scale-[2]">
    {LETTERS.map((letter, idx) => (
      <span
        key={idx}
        className="relative inline-block opacity-0 z-[2] text-black dark:text-white"
        style={{
          animation: "loadingLinesLetter 4s linear infinite",
          animationDelay: `${0.1 + idx * 0.105}s`,
        }}
      >
        {letter}
      </span>
    ))}

    {/* Scan bars container — vertical mask gives the "scanlines" effect.
        The inner colored blob translates across, opacity-pulses on its own
        cycle, and gets visible only through the bar mask. */}
    <div
      className="absolute top-0 left-0 w-full h-full z-[1] bg-transparent"
      style={{
        WebkitMaskImage:
          "repeating-linear-gradient(90deg, transparent 0, transparent 6px, black 7px, black 8px)",
        maskImage:
          "repeating-linear-gradient(90deg, transparent 0, transparent 6px, black 7px, black 8px)",
      }}
    >
      <div
        className="absolute top-0 left-0 w-full h-full"
        style={{
          backgroundImage: [
            "radial-gradient(circle at 50% 50%, #ff0 0%, transparent 50%)",
            "radial-gradient(circle at 45% 45%, #f00 0%, transparent 45%)",
            "radial-gradient(circle at 55% 55%, #0ff 0%, transparent 45%)",
            "radial-gradient(circle at 45% 55%, #0f0 0%, transparent 45%)",
            "radial-gradient(circle at 55% 45%, #00f 0%, transparent 45%)",
          ].join(", "),
          WebkitMaskImage:
            "radial-gradient(circle at 50% 50%, transparent 0%, transparent 10%, black 25%)",
          maskImage:
            "radial-gradient(circle at 50% 50%, transparent 0%, transparent 10%, black 25%)",
          animation:
            "loadingLinesTransform 2s infinite alternate cubic-bezier(0.6,0.8,0.5,1), loadingLinesOpacity 4s infinite",
        }}
      />
    </div>
  </div>
);

const LoadingLines = ({ fullScreen = false }) => {
  if (!fullScreen) return <LoadingLinesInner />;

  const overlay = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white dark:bg-[#060B12]">
      <LoadingLinesInner />
    </div>
  );

  if (typeof document !== "undefined") {
    return createPortal(overlay, document.body);
  }
  return overlay;
};

export default LoadingLines;
